import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fulfillmentApi } from '../../api/fulfillment-api';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { SearchableSelect } from '../../components/SearchableSelect';
import type { FulfillmentOrder } from '../../domain/fulfillment';
import { siteSearchAliases } from '../../lib/site-label';
import { fulfillmentErrorMessage, isUnknownOutcome } from './error-message';
import { RequirementsEditor } from './RequirementsEditor';
import { requirementRowsError, type RequirementRow } from './requirement-rows';
import { useFulfillmentReferences } from './use-fulfillment-references';

/** What a new order starts with when it's made for a Field Work Request. */
export interface NewOrderDefaults {
  customerId?: string | undefined;
  siteId?: string | undefined;
  fieldWorkRequestId: string;
  fieldWorkRequestTitle: string;
}

/** Creates a DRAFT order from a template and/or ad-hoc lines. */
export function NewFulfillmentOrderDrawer({
  defaults,
  onClose,
  onCreated,
}: {
  defaults?: NewOrderDefaults | undefined;
  onClose: () => void;
  onCreated: (order: FulfillmentOrder) => void;
}) {
  const queryClient = useQueryClient();
  const references = useFulfillmentReferences();
  const templates = useQuery({
    queryKey: ['fulfillment-templates'],
    queryFn: () => fulfillmentApi.listTemplates(),
  });
  const [customerId, setCustomerId] = useState(defaults?.customerId ?? '');
  const [siteId, setSiteId] = useState(defaults?.siteId ?? '');
  const [externalReference, setExternalReference] = useState('');
  const [dispatchDate, setDispatchDate] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [rows, setRows] = useState<RequirementRow[]>([]);
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);

  const customerOptions = useMemo(
    () =>
      references.customers.map((customer) => ({
        value: customer.id,
        label: references.customerName(customer.id),
        aliases: [customer.name],
      })),
    [references],
  );
  const siteOptions = useMemo(
    () =>
      references.sites
        .filter((site) => site.customerId === customerId)
        .map((site) => ({
          value: site.id,
          label: references.siteName(site.id),
          aliases: siteSearchAliases(site),
        })),
    [references, customerId],
  );
  const selectedTemplate = templates.data?.find((template) => template.id === templateId);

  const create = useMutation({
    mutationFn: () =>
      fulfillmentApi.createOrder({
        customerId,
        siteId,
        externalReference: externalReference.trim() || undefined,
        fieldWorkRequestId: defaults?.fieldWorkRequestId,
        dispatchDate: dispatchDate
          ? new Date(`${dispatchDate}T08:00:00`).toISOString()
          : undefined,
        templateId: templateId || undefined,
        notes: notes.trim() || undefined,
        lines: rows.map((row) => ({ requirement: row.requirement, locationId: row.locationId })),
      }),
    onSuccess: async (order) => {
      await queryClient.invalidateQueries({ queryKey: ['fulfillment-orders'] });
      onCreated(order);
    },
    onError: (error: unknown) => {
      if (isUnknownOutcome(error)) {
        setUnknownOutcome(true);
        return;
      }
      setFormError(fulfillmentErrorMessage(error, 'The order could not be created.'));
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    if (!customerId || !siteId) {
      setFormError('Choose the customer and the site the goods are for.');
      return;
    }
    if (!templateId && rows.length === 0) {
      setFormError('Choose a template or add at least one line.');
      return;
    }
    const rowError = requirementRowsError(rows);
    if (rowError) {
      setFormError(rowError);
      return;
    }
    create.mutate();
  }

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer drawer-wide"
        aria-label="New fulfillment order"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="drawer-heading">
          <h2>New order</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close new order drawer"
          >
            ×
          </button>
        </div>
        {formError ? (
          <p role="alert" className="form-error">
            {formError}
          </p>
        ) : null}
        {unknownOutcome ? (
          <PartialFailureNotice
            onCheckCurrentState={() => {
              void queryClient.invalidateQueries({ queryKey: ['fulfillment-orders'] });
              onClose();
            }}
          />
        ) : null}

        {defaults ? (
          <p className="field-hint">For field work request: {defaults.fieldWorkRequestTitle}</p>
        ) : null}
        <label>
          Customer
          <SearchableSelect
            ariaLabel="Customer"
            value={customerId}
            onChange={(value) => {
              setCustomerId(value);
              setSiteId('');
            }}
            options={customerOptions}
            placeholder="Choose a customer"
          />
        </label>
        <label>
          Site
          <SearchableSelect
            ariaLabel="Site"
            value={siteId}
            onChange={setSiteId}
            options={siteOptions}
            placeholder={customerId ? 'Choose a site' : 'Choose a customer first'}
            disabled={!customerId}
          />
        </label>
        <div className="form-row">
          <label>
            Order form number
            <input
              value={externalReference}
              onChange={(event) => setExternalReference(event.target.value)}
              placeholder="e.g. OF-2026-104"
            />
          </label>
          <label>
            Dispatch date
            <input
              type="date"
              value={dispatchDate}
              onChange={(event) => setDispatchDate(event.target.value)}
            />
          </label>
        </div>
        <label>
          Template
          <SearchableSelect
            ariaLabel="Template"
            value={templateId}
            onChange={setTemplateId}
            options={[
              { value: '', label: 'No template' },
              ...(templates.data ?? []).map((template) => ({
                value: template.id,
                label: template.name,
              })),
            ]}
            placeholder="No template"
          />
        </label>
        {selectedTemplate ? (
          <p className="field-hint">
            Adds {selectedTemplate.requirements.length} lines from “{selectedTemplate.name}”. Later
            changes to the template don’t change this order.
          </p>
        ) : null}
        <RequirementsEditor
          label={selectedTemplate ? 'Additional lines' : 'Lines'}
          rows={rows}
          onChange={setRows}
          references={references}
          showLocations
        />
        <label>
          Notes
          <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={2} />
        </label>
        <div className="drawer-actions">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create draft order'}
          </button>
        </div>
      </form>
    </div>
  );
}
